const { REACT_APP_NAME } = process.env;

import { Avatar, Box, Button, CircularProgress, Fade, TextField, Typography, Tabs, Tab } from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import { TeamProgramsTable } from './TeamProgramsTable';
import { usePostBodyWithJwtMutation } from 'src/redux/api/cabinet';
import { useSelector } from 'react-redux';
import { AppState } from 'src/store/Store';
import { useSearchParams } from 'react-router-dom';
import PageContainer from 'src/components/container/PageContainer';

export const TeamProgramsPage = () => {
    const [partners, setPartners] = useState<any[]>([]);
    const { data } = useSelector((state: AppState) => state.app);
    const [checkUser, setCheckUser] = useState<number>(0);
    const [activeUser, setActiveUser] = useState<any>(null);
    const [refererId, setRefererId] = useState<number>(0);
    const [searchParams, setSearchParams] = useSearchParams();

    const searchParam = searchParams.get('search') || '';
    const allParam = parseInt(searchParams.get('all') || '1', 10);
    const userIdParam = parseInt(searchParams.get('userId') || '0', 10);

    const [searchInput, setSearchInput] = useState(searchParam);
    const [allState, setAllState] = useState(allParam);
    const [typingTimeout, setTypingTimeout] = useState<NodeJS.Timeout | null>(null);

    const [getPartnersData, { isLoading }] = usePostBodyWithJwtMutation();

    const updateUrlParams = useCallback((params: { search?: string; all?: number; userId?: number }) => {
        const newParams = new URLSearchParams(searchParams);

        if (params.search !== undefined) {
            newParams.set('search', params.search);
        }
        if (params.all !== undefined) {
            newParams.set('all', params.all.toString());
        }
        if (params.userId !== undefined) {
            newParams.set('userId', params.userId.toString());
        }

        setSearchParams(newParams);
    }, [searchParams, setSearchParams]);

    const updPersonInfoId = useCallback((id: number, refererId: number) => {
        setCheckUser(id);
        updateUrlParams({ userId: id });
    }, [updateUrlParams]);

    const getPartnersRequest = useCallback(async (userId: number, all: number, search: string) => {
        const jwt = localStorage.getItem('jwt');
        const route = 'team-program';
        const body = { user_id: userId, all, search };

        try {
            const payload = await getPartnersData({ body, jwt, route }).unwrap();
            setPartners(payload?.data.teams || []);
            setRefererId(payload?.data.info.referer_id || 0);
            setActiveUser(payload?.data.info || null);
        } catch (error: any) {
            alert(error?.data?.message || "Ошибка сервера");
            setActiveUser(null);
            setPartners([]);
        }
    }, [getPartnersData]);

    const handleSearchChange = useCallback((value: string) => {
        setSearchInput(value);

        if (typingTimeout) {
            clearTimeout(typingTimeout);
        }

        const newTimeout = setTimeout(() => {
            updateUrlParams({ search: value });
        }, 200);

        setTypingTimeout(newTimeout);
    }, [typingTimeout, updateUrlParams]);

    const handleTabChange = useCallback((event: React.SyntheticEvent, newValue: number) => {
        setAllState(newValue);
        updateUrlParams({ all: newValue });
    }, [updateUrlParams]);

    const handleSearchSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        getPartnersRequest(checkUser, allState, searchParam);
    };

    useEffect(() => {
        return () => {
            if (typingTimeout) {
                clearTimeout(typingTimeout);
            }
        };
    }, [typingTimeout]);

    useEffect(() => {
        if (data?.id) {
            const initialUserId = userIdParam || data.id;
            setCheckUser(initialUserId);

            if (!userIdParam) {
                updateUrlParams({ userId: initialUserId });
            }
        }
    }, [data?.id]);

    useEffect(() => {
        if (checkUser > 0) {
            getPartnersRequest(checkUser, allParam, searchParam);
        }
    }, [checkUser, allParam, searchParam]);

    return (
        <>
            <PageContainer title={`${REACT_APP_NAME} | Уровни партнёров`} description="Уровни партнёров">
                <Typography variant='h4'>Мои партнёры</Typography>

                <Fade
                    in={!isLoading && data?.id !== activeUser?.id}
                    timeout={300}
                    mountOnEnter
                    unmountOnExit
                >
                    <Box display='flex' alignItems='center' justifyContent='space-between' mt={2}>
                        <Box>
                            <Button
                                size="large"
                                sx={{ mr: 1 }}
                                color="primary"
                                variant="contained"
                                onClick={() => updPersonInfoId(data?.id || 0, 0)}
                            >
                                В начало
                            </Button>
                            <Button
                                size="large"
                                variant="contained"
                                color="success"
                                onClick={() => updPersonInfoId(refererId, 0)}
                            >
                                Назад
                            </Button>
                        </Box>
                    </Box>
                </Fade>

                {activeUser && (
                    <Fade in={!isLoading} timeout={300}>
                        <div>
                            <Box display='flex' alignItems="center" mt={2}>
                                <Avatar
                                    sx={{ mr: 1 }}
                                    src={activeUser.avatar || ''}
                                    alt={activeUser.login}
                                />
                                <Typography variant='h5'>{activeUser.login}</Typography>
                            </Box>

                            <Box mt={3}>
                                <Tabs value={allState} onChange={handleTabChange} aria-label="all tabs">
                                    <Tab label="Все партнёры" value={1} />
                                    <Tab label="Активные" value={2} />
                                    <Tab label="Неактивные" value={3} />
                                </Tabs>
                            </Box>

                            <Box mt={3} display="flex" gap={2} alignItems="center">
                                <form onSubmit={handleSearchSubmit} style={{ width: '100%' }}>
                                    <Box display='inline-flex' width='100%' alignItems='center'>
                                        <TextField
                                            label="Поиск по логину"
                                            variant="outlined"
                                            value={searchInput}
                                            onChange={(e) => handleSearchChange(e.target.value)}
                                            fullWidth
                                        />
                                        <Button
                                            type="submit"
                                            variant="contained"
                                            sx={{ ml: 1 }}
                                        >
                                            Искать
                                        </Button>
                                    </Box>
                                </form>
                            </Box>
                        </div>
                    </Fade>
                )}

                <Box pb={4}>
                    {isLoading ? (
                        <Fade in={isLoading} timeout={300}>
                            <Box display='flex' justifyContent='center' alignItems='center' minHeight={500}>
                                <CircularProgress />
                            </Box>
                        </Fade>
                    ) : (
                        <>
                            {partners.length > 0 ? (
                                <Fade in={true} timeout={300}>
                                    <Box>
                                        <TeamProgramsTable partners={partners} updPersonInfoId={updPersonInfoId} />
                                    </Box>
                                </Fade>
                            ) : (
                                <Box display='flex' justifyContent='center' alignItems='center' minHeight={300}>
                                    <Typography variant='h6'>Партнёров нет</Typography>
                                </Box>
                            )}
                        </>
                    )}
                </Box>
            </PageContainer>
        </>
    );
};